import { Box, Typography } from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { endPoint } from "../../global";
import { useAuth } from "../../context/AuthProvider";
import GoldBackground from "../../components/GoldBackground";

export default function GrandPrize() {
  const { token } = useAuth();

  const [prizes, setPrizes] = useState([]);
  const [ticketNumber, setTicketNumber] = useState("00000000");
  const [winSpin, setWinSpin] = useState({});
  const [pendingWinner, setPendingWinner] = useState(null);

  const [isRolling, setIsRolling] = useState(false);
  const [rollingInterval, setRollingInterval] = useState(null);
  const [spinStep, setSpinStep] = useState(0);
  const [hadiahIndex, setHadiahIndex] = useState(0);

  const spinSoundRef = useRef(null);
  const winSoundRef = useRef(null);
  const isRollingRef = useRef(false);

  const grandPrizes = useMemo(() => {
    return prizes
      .filter((item) => item.type === "grandprize")
      .sort((a, b) => Number(a.prizeOrder) - Number(b.prizeOrder));
  }, [prizes]);

  const currentPrize = grandPrizes[hadiahIndex];

  const currentPrizeName =
    winSpin.prizeName ||
    pendingWinner?.prizeName ||
    currentPrize?.prizeName ||
    "Semua grand prize sudah keluar";

  const fetchInitialData = async () => {
    try {
      const [prizeResponse, winnerResponse] = await Promise.all([
        axios.get(endPoint + "getPrize", {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(endPoint + "getGrandWinner", {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      const prizeList = prizeResponse.data.data || [];
      const winnerList = winnerResponse.data.data || [];

      setPrizes(prizeList);
      setHadiahIndex(winnerList.length);
    } catch (error) {
      console.error("Error fetching initial data:", error);
      alert(
        error.response?.data?.message || "Gagal mengambil data grand prize.",
      );
    }
  };

  useEffect(() => {
    fetchInitialData();

    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const handleKeyDown = async (e) => {
      if (e.key !== "Enter" || isRolling) return;

      if (spinStep === 0 && currentPrize) {
        await startRolling();
      } else if (spinStep === 1) {
        stopRolling();
      } else if (spinStep === 2) {
        await resetSpin();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    spinStep,
    rollingInterval,
    hadiahIndex,
    currentPrize,
    isRolling,
    pendingWinner,
  ]);

  const generateRandomTicket = () => {
    const numbers = Math.floor(10000000 + Math.random() * 90000000);
    return `${numbers}`;
  };

  const playSpinSound = () => {
    if (!spinSoundRef.current) return;

    spinSoundRef.current.loop = false;
    spinSoundRef.current.currentTime = 0;
    spinSoundRef.current.play();

    spinSoundRef.current.onended = () => {
      if (isRollingRef.current) {
        spinSoundRef.current.currentTime = 0;
        spinSoundRef.current.play();
      }
    };
  };

  const stopSpinSound = () => {
    if (!spinSoundRef.current) return;

    spinSoundRef.current.onended = null;
    spinSoundRef.current.pause();
    spinSoundRef.current.currentTime = 0;
  };

  const startRolling = async () => {
    if (!currentPrize) {
      alert("Semua grand prize sudah keluar.");
      return;
    }

    setIsRolling(true);
    setWinSpin({});
    setPendingWinner(null);

    try {
      // HIT API DI ENTER PERTAMA
      const response = await axios.get(endPoint + "spinGrandWinner", {
        headers: { Authorization: `Bearer ${token}` },
      });

      const winnerData = response.data.data;

      setPendingWinner(winnerData);
      isRollingRef.current = true;

      playSpinSound();

      const interval = setInterval(() => {
        setTicketNumber(generateRandomTicket());
      }, 100);

      setRollingInterval(interval);
      setSpinStep(1);
    } catch (error) {
      console.error("Error prepare grand prize spin:", error);
      alert(error.response?.data?.message || "Terjadi kesalahan saat spin.");
      setSpinStep(0);
      setPendingWinner(null);
    } finally {
      setIsRolling(false);
    }
  };

  const stopRolling = () => {
    if (!pendingWinner) {
      alert("Pemenang belum siap. Coba tekan Enter lagi.");
      return;
    }

    clearInterval(rollingInterval);
    setIsRolling(true);
    isRollingRef.current = false;
    stopSpinSound();

    setTicketNumber(pendingWinner.coupon);
    setWinSpin(pendingWinner);
    winSoundRef.current?.play();

    setSpinStep(2);
    setIsRolling(false);
  };

  const resetSpin = async () => {
    try {
      setIsRolling(true);

      const [prizeResponse, winnerResponse] = await Promise.all([
        axios.get(endPoint + "getPrize", {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(endPoint + "getGrandWinner", {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      const prizeList = prizeResponse.data.data || [];
      const winnerList = winnerResponse.data.data || [];

      setPrizes(prizeList);
      setTicketNumber("00000000");
      setWinSpin({});
      setPendingWinner(null);
      setSpinStep(0);
      setHadiahIndex(winnerList.length);
    } catch (error) {
      console.error("Error fetching grand winner on reset:", error);
      alert(
        error.response?.data?.message ||
          "Gagal mengambil daftar pemenang grand prize.",
      );
    } finally {
      setIsRolling(false);
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: "#050d1f",
        width: "100%",
        minHeight: "100vh",
        position: "relative",
        overflow: "hidden",
        backgroundImage: `linear-gradient(to bottom, transparent 45%, rgba(5,13,31,0.85) 75%, #050d1f 100%), url("/Background%20Scenery.jpg")`,
        backgroundSize: "cover",
        backgroundPosition: "center top",
        backgroundRepeat: "no-repeat",
      }}
    >
      <audio ref={spinSoundRef} src="/spin.mp3" preload="auto" />
      <audio ref={winSoundRef} src="/win.mp3" preload="auto" />

      <GoldBackground />

      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        sx={{
          position: "relative",
          zIndex: 2,
          p: 3,
          minHeight: "100vh",
        }}
      >
        <img
          src="SolarTuffNatGath.png"
          alt="Solartuff NatGath"
          style={{ width: 260, objectFit: "contain" }}
        />

        <Typography
          sx={{
            fontFamily: "'Poppins', sans-serif",
            fontWeight: 800,
            fontSize: "3rem",
            color: "rgba(255,255,255,0.88)",
            letterSpacing: { xs: 1.5, md: 2.5 },
            textAlign: "center",
            mb: 2,
            mt: 2,
            textTransform: "uppercase",
            textShadow: `
             0 0 8px rgba(96,165,250,0.8),
             0 0 18px rgba(96,165,250,0.55),
             0 0 36px rgba(59,130,246,0.35)
           `,
            animation: "pulseTitle 2.5s ease-in-out infinite alternate",
            "@keyframes pulseTitle": {
              from: {
                textShadow: `
                 0 0 7px rgba(96,165,250,0.65),
                 0 0 18px rgba(59,130,246,0.45),
                 0 0 34px rgba(37,99,235,0.25)
               `,
              },
              to: {
                textShadow: `
                 0 0 12px rgba(186,230,253,0.9),
                 0 0 28px rgba(96,165,250,0.7),
                 0 0 52px rgba(59,130,246,0.45)
               `,
              },
            },
          }}
        >
          GRAND PRIZE
        </Typography>

        <Typography
          sx={{
            fontFamily: "'Poppins', sans-serif",
            color: "white",
            fontWeight: 900,
            fontSize: "2.3rem",
            lineHeight: 1.05,
            mb: 4,
            textAlign: "center",
          }}
        >
          {currentPrizeName}
        </Typography>

        <Typography
          sx={{
            fontWeight: "bold",
            fontSize: { xs: "4rem", md: "5rem" },
            color: "#93c5fd",
            textAlign: "center",
            background:
              "linear-gradient(#050d1f, #091b3d) padding-box, linear-gradient(90deg, #3b82f6, #60a5fa) border-box",
            border: "6px solid transparent",
            borderRadius: "20px",
            padding: "24px 48px",
            marginBottom: "32px",
            boxShadow: "0 0 30px rgba(59, 130, 246, 0.7)",
          }}
        >
          {ticketNumber}
        </Typography>

        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          sx={{
            minHeight: { xs: "150px", md: "200px" },
            justifyContent: "center",
            animation: winSpin.name ? "fadeSlideIn 1s ease-out" : "none",
            "@keyframes fadeSlideIn": {
              from: { opacity: 0, transform: "translateY(20px)" },
              to: { opacity: 1, transform: "translateY(0)" },
            },
          }}
        >
          {winSpin.name ? (
            <>
              <Typography
                sx={{
                  fontSize: { xs: "3rem", md: "4rem" },
                  fontWeight: "bold",
                  color: "#60a5fa",
                  textAlign: "center",
                  animation: "glowWinner 1.5s infinite alternate",
                  "@keyframes glowWinner": {
                    from: {
                      textShadow: "0 0 8px rgba(96, 165, 250, 0.6)",
                    },
                    to: {
                      textShadow: "0 0 28px rgba(96, 165, 250, 1)",
                    },
                  },
                }}
              >
                {winSpin.name}
              </Typography>

              <Typography
                sx={{
                  fontSize: { xs: "2rem", md: "2.5rem" },
                  fontWeight: 500,
                  color: "white",
                  textAlign: "center",
                  mt: 1,
                }}
              >
                {winSpin.storeName || "-"}
              </Typography>
            </>
          ) : (
            <>
              <Typography
                sx={{ fontSize: { xs: "3rem", md: "4rem" }, opacity: 0 }}
              >
                Nama
              </Typography>

              <Typography
                sx={{ fontSize: { xs: "2rem", md: "2.5rem" }, opacity: 0 }}
              >
                Toko
              </Typography>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
}
